"use client";

import { Flag, ListChecks, Lightbulb, Sparkles, Waypoints, CircleCheck } from "lucide-react";
import { cn } from "@/utils/cn";
import { useState } from "react";
import { Locale } from "@/i18n-config";
import { getLandingCopy } from "@/content/landing-copy";

const stepIcons = [
    Lightbulb,
    Flag,
    ListChecks,
    Sparkles,
    Waypoints,
    CircleCheck,
];

type StaticPathSectionProps = {
    lang: Locale;
};

export function StaticPathSection({ lang }: StaticPathSectionProps) {
    const [hoveredStep, setHoveredStep] = useState<number | null>(null);
    const copy = getLandingCopy(lang).path;

    return (
        <section
            id="path-start"
            className="focus-target overflow-hidden border-b border-[#d9cfbe] bg-[#f4efe3] py-20 md:py-24"
        >
            <div className="container relative">
                <div className="mb-16 text-center">
                    <h2 className="text-3xl font-bold text-[#1b2220] md:text-5xl">
                        {copy.title}
                    </h2>
                    <p className="mx-auto mt-5 max-w-2xl text-lg text-[#374842]">
                        {copy.description}
                    </p>
                </div>

                {/* Desktop Path */}
                <div className="hidden lg:block relative py-12">
                    {/* Connecting Line */}
                    <div className="absolute top-[34px] left-[5%] right-[5%] h-[2px] -translate-y-1/2 overflow-hidden rounded-full bg-[#d5c6aa]">
                        <div className="absolute inset-0 w-1/3 animate-shimmer bg-gradient-to-r from-[#0f766e]/0 via-[#0f766e]/40 to-[#0f766e]/0" />
                    </div>

                    <div className="grid grid-cols-6 gap-4 relative z-10">
                        {copy.steps.map((step, index) => {
                            const StepIcon = stepIcons[index];
                            return (
                            <div
                                key={step.title}
                                className="group relative flex flex-col items-center text-center px-2"
                                onMouseEnter={() => setHoveredStep(index)}
                                onMouseLeave={() => setHoveredStep(null)}
                            >
                                <div className={cn(
                                    "relative z-10 mb-6 flex h-16 w-16 items-center justify-center rounded-full border-4 border-[#f4efe3] bg-[#fffaf0] shadow-lg transition-all duration-300",
                                    hoveredStep === index
                                        ? "scale-110 border-[#0f766e] bg-[#0f766e] text-white shadow-[0_0_0_8px_rgba(15,118,110,0.18)]"
                                        : "border-[#d5c8b2] text-[#355046]"
                                )}>
                                    <StepIcon size={26} />
                                </div>

                                <h3 className={cn(
                                    "mb-2 text-lg font-bold transition-colors",
                                    hoveredStep === index ? "text-[#1b2623]" : "text-[#2d3b37]"
                                )}>
                                    {step.title}
                                </h3>
                                <p className="text-sm leading-relaxed text-[#566761] opacity-80 transition-opacity group-hover:opacity-100">
                                    {step.desc}
                                </p>
                            </div>
                        )})}
                    </div>
                </div>

                {/* Mobile Path (Vertical) */}
                <div className="lg:hidden relative pl-4">
                    <div className="absolute top-4 bottom-12 left-[27px] w-[2px] bg-[#d5c8b2]" />
                    <div className="space-y-10">
                        {copy.steps.map((step, index) => {
                            const StepIcon = stepIcons[index];
                            return (
                            <div key={step.title} className="relative flex items-center gap-6">
                                <div className={cn(
                                    "relative z-10 flex h-14 w-14 shrink-0 items-center justify-center rounded-full border-4 border-[#f4efe3] bg-[#fffaf0] text-[#355046] shadow-md ring-1 ring-[#d5c8b2]"
                                )}>
                                    <StepIcon size={22} />
                                </div>
                                <div className="flex-1">
                                    <h3 className="mb-1 text-lg font-bold text-[#1f2a27]">{step.title}</h3>
                                    <p className="text-[#566761]">{step.desc}</p>
                                </div>
                            </div>
                        )})}
                    </div>
                </div>
            </div>
        </section>
    );
}
